import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useId } from 'react';
import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import LanguageIcon from '@mui/icons-material/Language';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES } from '../i18n';
export const LanguageSelector = () => {
    const { i18n } = useTranslation();
    const [anchorEl, setAnchorEl] = useState(null);
    const popoverId = useId();
    const current = SUPPORTED_LANGUAGES.find((l) => l.code === i18n.language) ?? SUPPORTED_LANGUAGES[0];
    const handleOpen = (e) => setAnchorEl(e.currentTarget);
    const handleClose = () => setAnchorEl(null);
    const handleSelect = (code) => {
        i18n.changeLanguage(code);
        localStorage.setItem('pondlogs_locale', code);
        handleClose();
    };
    const open = Boolean(anchorEl);
    return (_jsxs(_Fragment, { children: [_jsxs(Box, { component: "button", onClick: handleOpen, "aria-haspopup": "listbox", "aria-expanded": open, "aria-controls": open ? popoverId : undefined, sx: {
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    m: 0,
                    px: 1.5,
                    py: 0.75,
                    width: 'fit-content',
                    borderRadius: '999px',
                    border: 'none',
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    bgcolor: 'transparent',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    transition: 'background 0.15s',
                    outline: 'none',
                    '&:hover': { bgcolor: 'grey.100' },
                }, children: [_jsx(LanguageIcon, { sx: { fontSize: 15, color: 'primary.main', flexShrink: 0 } }), _jsx(Typography, { component: "span", sx: {
                            fontSize: 14,
                            fontWeight: 700,
                            color: 'primary.main',
                            lineHeight: 1,
                            userSelect: 'none',
                        }, children: current.name }), _jsx(KeyboardArrowDownIcon, { sx: {
                            fontSize: 16,
                            color: 'primary.main',
                            flexShrink: 0,
                            transition: 'transform 0.2s',
                            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                        } })] }), _jsx(Popover, { id: popoverId, open: open, anchorEl: anchorEl, onClose: handleClose, disableScrollLock: true, anchorOrigin: { vertical: 'bottom', horizontal: 'right' }, transformOrigin: { vertical: 'top', horizontal: 'right' }, slotProps: {
                    paper: {
                        sx: {
                            mt: 0.75,
                            minWidth: 180,
                            borderRadius: '14px',
                            border: '1px solid #e8edf2',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.10)',
                            overflow: 'hidden',
                        },
                    },
                }, children: SUPPORTED_LANGUAGES.map((lang, i) => {
                    const isActive = lang.code === i18n.language;
                    return (_jsxs(Box, { component: "button", role: "option", "aria-selected": isActive, onClick: () => handleSelect(lang.code), sx: {
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.25,
                            width: '100%',
                            m: 0,
                            px: 2,
                            py: 1.125,
                            border: 'none',
                            appearance: 'none',
                            WebkitAppearance: 'none',
                            bgcolor: isActive ? '#003C710d' : 'transparent',
                            cursor: 'pointer',
                            fontFamily: 'inherit',
                            textAlign: 'left',
                            transition: 'background 0.12s',
                            '&:hover': { bgcolor: isActive ? '#003C711a' : '#f4f6f8' },
                            // subtle separator between items except last
                            ...(i < SUPPORTED_LANGUAGES.length - 1 && {
                                borderBottom: '1px solid #f0f2f5',
                            }),
                        }, children: [_jsx(Typography, { component: "span", sx: {
                                    flex: 1,
                                    fontSize: 13,
                                    fontWeight: isActive ? 700 : 500,
                                    color: isActive ? '#003C71' : '#1a2233',
                                    lineHeight: 1,
                                }, children: lang.name }), isActive && _jsx(CheckIcon, { sx: { fontSize: 14, color: '#003C71', flexShrink: 0 } })] }, lang.code));
                }) })] }));
};
